/**
 * Calculator Form State Hook
 * 
 * Manages form state, validation, and submission for the calculator.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  MediumKey,
  TierKey,
  CareerStageKey,
  VerdictInputs,
  DEFAULT_PROFILE,
} from '@/types';
import {
  validateDimension,
  validateHours,
  validateMaterials,
  validateGutPrice,
} from '@/lib';
import type { DepthValue } from '@/components/calculator/DimensionInput';

export interface CalculatorFormState {
  // Artwork inputs
  medium: MediumKey;
  height: number | '';
  width: number | '';
  depth: DepthValue;  // For portfolio records (doesn't affect TTCP calculation)
  hours: number | '';
  materials: number | null;  // cents
  gutPrice: number | null;   // cents
  tier: TierKey;
  
  // Profile context (from user profile or defaults)
  monthlyBurn: number;       // cents
  monthlyCapacity: number;   // hours
  careerStage: CareerStageKey;
  highestSale: number | null; // cents
}

export interface CalculatorFormErrors {
  height?: string;
  width?: string;
  hours?: string;
  materials?: string;
  gutPrice?: string;
}

export interface UseCalculatorFormReturn {
  // State
  formState: CalculatorFormState;
  errors: CalculatorFormErrors;
  isValid: boolean;
  isDirty: boolean;
  
  // Actions
  setField: <K extends keyof CalculatorFormState>(field: K, value: CalculatorFormState[K]) => void;
  setMedium: (medium: MediumKey) => void;
  setTier: (tier: TierKey) => void;
  setHeight: (height: number | '') => void;
  setWidth: (width: number | '') => void;
  setDepth: (depth: DepthValue) => void;
  setHours: (hours: number | '') => void;
  setMaterials: (cents: number | null) => void;
  setGutPrice: (cents: number | null) => void;
  
  // Submission
  validate: () => boolean;
  getVerdictInputs: () => VerdictInputs | null;
  reset: () => void;
}

const initialState: CalculatorFormState = {
  medium: DEFAULT_PROFILE.defaultMedium,
  height: 24,
  width: 30,
  depth: '1.5',  // Default to gallery wrap
  hours: '',
  materials: null,
  gutPrice: null,
  tier: DEFAULT_PROFILE.defaultTier,
  monthlyBurn: DEFAULT_PROFILE.monthlyBurn,
  monthlyCapacity: DEFAULT_PROFILE.monthlyCapacity,
  careerStage: DEFAULT_PROFILE.careerStage,
  highestSale: null,
};

export function useCalculatorForm(
  profileOverrides?: Partial<Pick<CalculatorFormState, 'monthlyBurn' | 'monthlyCapacity' | 'careerStage' | 'highestSale'>>
): UseCalculatorFormReturn {
  const [formState, setFormState] = useState<CalculatorFormState>(() => ({
    ...initialState,
    ...profileOverrides,
  }));
  
  const [errors, setErrors] = useState<CalculatorFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  
  // Generic field setter
  const setField = useCallback(<K extends keyof CalculatorFormState>(
    field: K,
    value: CalculatorFormState[K]
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    
    // Clear error for this field
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);
  
  // Convenience setters
  const setMedium = useCallback((medium: MediumKey) => setField('medium', medium), [setField]);
  const setTier = useCallback((tier: TierKey) => setField('tier', tier), [setField]);
  const setHeight = useCallback((height: number | '') => setField('height', height), [setField]);
  const setWidth = useCallback((width: number | '') => setField('width', width), [setField]);
  const setDepth = useCallback((depth: DepthValue) => setField('depth', depth), [setField]);
  const setHours = useCallback((hours: number | '') => setField('hours', hours), [setField]);
  const setMaterials = useCallback((cents: number | null) => setField('materials', cents), [setField]);
  const setGutPrice = useCallback((cents: number | null) => setField('gutPrice', cents), [setField]);
  
  // Validate all fields
  const validate = useCallback((): boolean => {
    const newErrors: CalculatorFormErrors = {};
    
    const heightError = validateDimension(formState.height === '' ? null : formState.height);
    if (heightError) newErrors.height = heightError;
    
    const widthError = validateDimension(formState.width === '' ? null : formState.width);
    if (widthError) newErrors.width = widthError;
    
    const hoursError = validateHours(formState.hours === '' ? null : formState.hours);
    if (hoursError) newErrors.hours = hoursError;
    
    const materialsError = validateMaterials(formState.materials);
    if (materialsError) newErrors.materials = materialsError;
    
    const gutPriceError = validateGutPrice(formState.gutPrice);
    if (gutPriceError) newErrors.gutPrice = gutPriceError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);
  
  // Check if form is valid (for enabling submit button)
  const isValid = useMemo(() => {
    // Required fields must have values
    if (formState.height === '' || formState.height <= 0) return false;
    if (formState.width === '' || formState.width <= 0) return false;
    if (formState.hours === '' || formState.hours <= 0) return false;
    if (formState.materials === null) return false;
    if (formState.gutPrice === null || formState.gutPrice <= 0) return false;

    return true;
  }, [formState]);
  
  // Convert form state to verdict inputs
  const getVerdictInputs = useCallback((): VerdictInputs | null => {
    if (!validate()) return null;
    
    const height = formState.height === '' ? 0 : formState.height;
    const width = formState.width === '' ? 0 : formState.width;
    const hours = formState.hours === '' ? 0 : formState.hours;
    
    return {
      medium: formState.medium,
      height,
      width,
      hours,
      materials: formState.materials ?? 0,
      gutPrice: formState.gutPrice,
      tier: formState.tier,
      monthlyBurn: formState.monthlyBurn,
      monthlyCapacity: formState.monthlyCapacity,
      careerStage: formState.careerStage,
      highestSale: formState.highestSale,
      currency: 'USD',
    };
  }, [formState, validate]);
  
  // Reset form
  const reset = useCallback(() => {
    setFormState({ ...initialState, ...profileOverrides });
    setErrors({});
    setIsDirty(false);
  }, [profileOverrides]);
  
  return {
    formState,
    errors,
    isValid,
    isDirty,
    setField,
    setMedium,
    setTier,
    setHeight,
    setWidth,
    setDepth,
    setHours,
    setMaterials,
    setGutPrice,
    validate,
    getVerdictInputs,
    reset,
  };
}

export default useCalculatorForm;
